import type {
  HabitaProcessDetail,
  HabitaProcessListItem,
  PastaStatus,
} from '../../shared/models';
import { pastaStatusToApiStatus } from '../../shared/habitaStatusPresentation';
import type { ProcessSortOrder } from '../../shared/utils/processOrdering';
import { ProcessRepository } from './ProcessRepository';

export interface PastasPageParams {
  page: number;
  page_size: number;
  status?: PastaStatus;
  searchText?: string;
  processoText?: string;
  responsibleEmail?: string;
  startDate?: string;
  endDate?: string;
  sortOrder?: ProcessSortOrder;
}

export interface PastasPageResult {
  items: HabitaProcessListItem[];
  total: number;
  hasExplicitTotal: boolean;
  hasNextPage: boolean;
}

function normalizeProcessIdentifier(value: string): string {
  return value.replace(/\D/g, '');
}

function isSameProcessIdentifier(left: string, right: string): boolean {
  if (left === right) return true;

  const normalizedLeft = normalizeProcessIdentifier(left);
  const normalizedRight = normalizeProcessIdentifier(right);
  return normalizedLeft !== '' && normalizedLeft === normalizedRight;
}

function loadedCountLowerBound(params: Pick<PastasPageParams, 'page' | 'page_size'>, itemCount: number): number {
  return ((params.page - 1) * params.page_size) + itemCount;
}

export class PastasRepository {
  private processRepository = new ProcessRepository();

  async fetchPastasPage(params: PastasPageParams): Promise<PastasPageResult> {
    const apiStatus = params.status ? pastaStatusToApiStatus(params.status) : undefined;
    if (params.status && !apiStatus) {
      return {
        items: [],
        total: 0,
        hasExplicitTotal: true,
        hasNextPage: false,
      };
    }

    const searchText = params.searchText?.trim();
    const processoText = params.processoText?.trim();
    const responsibleEmail = params.responsibleEmail?.trim();
    const result = await this.processRepository.findAllWithCustomer({
      page: params.page,
      page_size: params.page_size,
      ...(apiStatus ? { status: apiStatus } : {}),
      ...(searchText ? { searchText } : {}),
      ...(processoText ? { processoText } : {}),
      ...(responsibleEmail ? { responsibleEmail } : {}),
      ...(params.startDate ? { startDate: params.startDate } : {}),
      ...(params.endDate ? { endDate: params.endDate } : {}),
      ...(params.sortOrder ? { sortOrder: params.sortOrder } : {}),
    });
    const items = result.items;
    const hasExplicitTotal = Boolean(result.hasExplicitTotal);

    return {
      items,
      total: hasExplicitTotal ? result.total : loadedCountLowerBound(params, items.length),
      hasExplicitTotal,
      hasNextPage: hasExplicitTotal
        ? params.page * params.page_size < result.total
        : result.items.length === params.page_size,
    };
  }

  async fetchPastaById(id: string): Promise<HabitaProcessDetail | null> {
    const normalizedProcessId = normalizeProcessIdentifier(id) || id;
    return this.processRepository.findByIdWithCustomer(normalizedProcessId);
  }

  async fetchPastaByProcessoId(processoId: string): Promise<HabitaProcessDetail | null> {
    const normalizedProcessoId = normalizeProcessIdentifier(processoId);
    const detail = await this.processRepository.findByIdWithCustomer(normalizedProcessoId || processoId);
    if (!detail || !isSameProcessIdentifier(String(detail.id ?? ''), processoId)) return null;

    return detail;
  }
}
